/**
 * The Claude chat demo from the homepage (landing/index.html), for Learn →
 * Using Claude: five worked examples that play one after another — the
 * question types itself, the MarketMapr steps tick off, the answer arrives
 * block by block. Tabs jump to an example. Made-up people and companies.
 * Keep the examples in step with the homepage's.
 */
import React, { useEffect, useRef, useState } from 'react';
import './claudeChatDemo.css';

interface Scene { tab: string; q: string; file?: string; steps: string[]; blocks: string[] }

const row = (n: number, name: string, role: string, why: string) =>
  `<div class="cl-row"><span class="n">${n}</span><span class="who"><b>${name}</b><span>${role}</span></span><span class="why">${why}</span></div>`;
const bar = (label: string, v: number, max: number) =>
  `<div class="cl-bar-r"><span>${label}</span><span class="track"><span class="fill" style="width:${(v / max * 100).toFixed(1)}%"></span></span><span class="v">${v}%</span></div>`;

export const SCENES: Scene[] = [
  {
    tab: 'Job spec → shortlist',
    q: 'Here’s a job spec from Ashby Kerr for a Corporate Tax Manager, financial services. Use MarketMapr to find me the best candidates and put them in a project.',
    file: 'Ashby Kerr – Corporate Tax Manager (FS).pdf',
    steps: [
      'Read the job spec: <b>CTA/ACA, FS exposure, BD experience</b>',
      'Searched 431M profiles: <b>177 strong UK matches</b>',
      'Read the career histories of the top matches',
      'Created project <b>“Ashby Kerr – Corporate Tax Manager (FS)”</b>',
    ],
    blocks: [
      `<p class="cl-p">There are <b>177 strong matches</b> in the UK. I’ve split them into two groups:</p>`,
      `<div class="cl-cards">
        <div class="cl-card"><div class="big">47</div><div class="t">FS tax – practice &amp; industry (qualified)</div><div class="s">At Big 4 and mid-tier firms, already doing FS corporate tax.</div></div>
        <div class="cl-card"><div class="big">130</div><div class="t">In-house FS tax – ex-practice</div><div class="s">At banks, insurers and asset managers, trained in practice.</div></div>
      </div>`,
      `<p class="cl-p">The best five to call first:</p>`,
      `<div class="cl-rows">
        ${row(1, 'James Cooper', 'Corporate Tax Manager · Big 4', 'CTA, six years of FS corporate tax, runs a banking client portfolio.')}
        ${row(2, 'Tony Evans', 'Tax Manager · mid-tier firm', 'ACA/CTA, insurance groups, already leads proposals and pitches.')}
        ${row(3, 'Emma Morray', 'Senior Tax Manager · asset manager', 'Big 4 trained, three years in-house on fund structures.')}
        ${row(4, 'Priya Desai', 'Assistant Tax Manager · Big 4', 'CTA, four years of banking corporate tax. Ready for the step up.')}
        ${row(5, 'Ollie Grant', 'Tax Manager · insurer', 'ACA, trained in practice, owns the group’s corporation tax returns.')}
      </div>`,
      `<div class="cl-proj"><span class="ic">📁</span><span><span class="t">Ashby Kerr – Corporate Tax Manager (FS)</span><br><span class="s">177 people · 2 groups · mapped</span></span><span class="go">Open in MarketMapr →</span></div>`,
      `<p class="cl-p">Next, I can score the list against the spec’s must-haves (CTA/ACA, FS exposure, BD experience) and get contact details for the top 20.</p>`,
    ],
  },
  {
    tab: 'Win a target account',
    q: 'I want to win Veridian Mission Critical as a client for data centre commissioning engineers. Who should I contact?',
    steps: [
      'Found <b>Veridian Mission Critical</b> and its people',
      'Mapped the commissioning leadership: <b>38 people</b>',
      'Checked start dates, moves and regions',
      'Found the talent acquisition team',
    ],
    blocks: [
      `<p class="cl-p">Start with the people who own commissioning delivery, then bring in talent acquisition.</p>`,
      `<div class="cl-rows">
        ${row(1, 'Daniel Hartley <span class="cl-tag new">New · May 2026</span>', 'Director of Commissioning, Americas', 'Responsible for developing people across the region. New leaders often look at new suppliers.')}
        ${row(2, 'Jay Fulton', 'Regional Director · Texas', 'Runs one of the two biggest build markets.')}
        ${row(3, 'Travis Novak', 'Regional Director · Arizona', 'Runs the other one.')}
      </div>`,
      `<div class="cl-note"><b>Ellie Jordan, Talent Acquisition Lead (London).</b> Bring her in once the hiring side is interested. If you go to TA first, you’ll probably be sent to their preferred supplier list.</div>`,
      `<p class="cl-p">Want me to add all four to a project and get their work emails? That’s 4 credits.</p>`,
    ],
  },
  {
    tab: 'Daily call sheet',
    q: 'I work the UK electrical design market. Every weekday morning, give me five people to call, why today, and what to say. Use MarketMapr data and news.',
    steps: [
      'Checked your monitored projects: <b>3 projects</b>',
      'Tested a search for each day’s focus',
      'Scheduled a task: <b>weekdays at 7:25am</b>',
    ],
    blocks: [
      `<p class="cl-p">Done. It runs <b>every weekday at 7:25am</b> and covers a different area each day:</p>`,
      `<div class="cl-week">
        <div class="cl-day"><b>Mon</b>Grid / HV</div><div class="cl-day"><b>Tue</b>Data centres</div><div class="cl-day"><b>Wed</b>MEP</div>
        <div class="cl-day"><b>Thu</b>Water &amp; renewables</div><div class="cl-day"><b>Fri</b>Wildcard</div>
      </div>`,
      `<p class="cl-p">Each morning I look for:</p>
       <ul class="cl-list"><li>teams that have grown fast in the last 12 months</li><li>new heads of design less than 9 months into the role</li><li>leavers in your monitored projects</li></ul>`,
      `<p class="cl-p">Then I check each company for contract wins, funding and new appointments, skip any company featured in the last 10 working days, and use only free tools.</p>`,
      `<div class="cl-card"><div class="s">Tomorrow’s doc · Tue · Data centres · with a push notification</div>
        <div class="t" style="margin-top:6px">1. Rachel Moss, Head of Electrical Design, Halcyon DC <span class="cl-tag new">4 months in role</span></div>
        <div class="s">Her team has grown 38% in 12 months, and Halcyon announced a new 60MW campus yesterday.</div>
        <div class="cl-quote">“Congratulations on the new campus. Most heads of design I speak to are adding to the team before the first phase. Is that where you are?”</div>
        <div class="s" style="margin-top:6px">Sources: MarketMapr · trade press, retrieved this morning</div></div>`,
    ],
  },
  {
    tab: 'Market report',
    q: 'Create a premium market intelligence report on Contract Salesforce Developers in the UK: where most people are based, top hiring companies, industry split and demographics.',
    steps: [
      'Counted the whole UK market',
      'Broke it down by city, company and industry',
      'Wrote the report: <b>7 pages</b>',
    ],
    blocks: [
      `<p class="cl-p">Here’s a 7-page report. The numbers are from MarketMapr as of tonight.</p>`,
      `<div class="cl-report">
        <div class="cl-cover"><span>MARKET REPORT · NOV 2026</span><b>UK Contract Salesforce Developers</b><span>Prepared for your client</span></div>
        <div>
          <div class="cl-bars"><div class="bt">Where they’re based</div>
            ${bar('London', 34, 34)}${bar('Milton Keynes', 7, 34)}${bar('Manchester', 6, 34)}${bar('Birmingham', 5, 34)}${bar('Reading', 4, 34)}
          </div>
          <div class="cl-pages"><span>1 Summary</span><span>2 Locations</span><span>3 Top hiring companies</span><span>4 Industry split</span><span>5 Demographics</span><span>6 Tenure &amp; movement</span><span>7 Method</span></div>
        </div>
      </div>`,
      `<p class="cl-p">Most work in IT services (52%) and software companies (28%); financial services is the biggest end-user industry. Want a version with your logo on the cover?</p>`,
    ],
  },
  {
    tab: 'Movers & backfills',
    q: 'Show me any Mechanical Design Engineers who have changed roles in the last 2 months, tell me the companies they have joined, as well as the companies they have left and find me the right contacts to reach out to.',
    steps: [
      'Found UK Mechanical Design Engineers in a new role since September: <b>186</b>',
      'Removed <b>41</b> internal promotions',
      'Grouped by the company joined and the company left',
      'Found the hiring managers',
      'Created project <b>“Mech Design Movers – Sep–Nov 2026”</b>',
    ],
    blocks: [
      `<p class="cl-p">186 started a new role since early September. I removed 41 who were promoted internally, because those teams haven’t lost anyone. That leaves <b>145 people who moved company</b>.</p>`,
      `<div class="cl-2col">
        <div class="cl-card"><div class="t">Companies hiring</div><div class="s">They’ve just recruited, so they may need more.</div>
          <ul><li><b>Kestrel Metrology</b>: 6 new starters in 8 weeks, 5 from outside. A team being built, not one replacement.</li><li><b>Arden Appliances</b>: 4 · <b>Fenwick Plant</b>: 3</li><li><b>Westcombe Aerospace</b> (Yeovil): 3</li></ul></div>
        <div class="cl-card"><div class="t">Companies with gaps</div><div class="s">They’ve just lost someone.</div>
          <ul><li><b>Northvale SMR</b>: 4 leavers, 2 of them to Kestrel.</li><li><b>Calder Vacuum</b>: 3 leavers from the Burgess Hill team, none replaced yet.</li><li><b>Marlowe Engineering</b>: 2 senior designers with 8+ years.</li></ul></div>
      </div>`,
      `<div class="cl-h">Who to contact</div>`,
      `<div class="cl-rows">
        ${row(1, 'Sarah Lewis <span class="cl-tag bf">Backfill</span>', 'Head of Mechanical Design · Calder Vacuum, Burgess Hill', 'Three of her team have left since September.<div class="cl-quote">“I noticed a few of your design team have moved on. I’ve mapped who’s available locally with vacuum and rotating equipment experience. Would a quick read-out be useful?”</div>')}
        ${row(2, 'Tom Hughes <span class="cl-tag gr">Growth</span>', 'Engineering Manager · Kestrel Metrology', 'Has hired six, mostly from aerospace and nuclear. Offer him the same background.')}
        ${row(3, 'Priya Shah <span class="cl-tag sr">Senior backfill</span>', 'Chief Engineer, Mechanical · Marlowe Engineering', 'Senior designers are hard to replace, so a shortlist from you is worth more here.')}
      </div>`,
      `<div class="cl-proj"><span class="ic">📁</span><span><span class="t">Mech Design Movers – Sep–Nov 2026</span><br><span class="s">145 people · tagged Joined / Left from</span></span><span class="go">Open in MarketMapr →</span></div>`,
      `<p class="cl-p">Next, I can get work emails for the 8 hiring managers I found (8 credits), or turn on monitoring for this project so future moves come to you.</p>`,
    ],
  },
];


const ClaudeChatDemo: React.FC = () => {
  const winRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const sendRef = useRef<HTMLSpanElement>(null);
  const [active, setActive] = useState(0);
  const startRef = useRef<(i: number, clicked: boolean) => void>(() => {});

  useEffect(() => {
    const win = winRef.current!, body = bodyRef.current!, thread = threadRef.current!, text = textRef.current!, send = sendRef.current!;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let run = 0;
    let visible = false;
    let alive = true;
    const wait = (ms: number, my: number) => new Promise<void>((res, rej) =>
      setTimeout(() => (my === run && alive ? res() : rej(new Error('stale'))), reduced ? 0 : ms));
    const waitVisible = async (my: number) => {
      while (!visible) {
        await new Promise((r) => setTimeout(r, 300));
        if (my !== run || !alive) throw new Error('stale');
      }
    };
    const scrollDown = () => {
      if (reduced) { body.scrollTop = body.scrollHeight; return; }
      body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
    };
    const add = (html: string) => {
      const d = document.createElement('div');
      d.className = 'cl-in';
      d.innerHTML = html;
      thread.appendChild(d);
      scrollDown();
      return d;
    };
    async function play(i: number, my: number) {
      const s = SCENES[i];
      thread.innerHTML = '';
      body.scrollTop = 0;
      text.innerHTML = '';
      const t = document.createElement('span');
      const caret = document.createElement('span');
      caret.className = 'ai-caret';
      text.append(t, caret);
      send.classList.add('on');
      if (reduced) t.textContent = s.q;
      else for (let k = 1; k <= s.q.length; k++) { t.textContent = s.q.slice(0, k); await wait(k % 9 ? 18 : 40, my); }
      await wait(450, my);
      text.innerHTML = '';
      send.classList.remove('on');
      const u = add('<div class="cl-user"></div>').firstChild as HTMLElement;
      u.textContent = s.q;
      if (s.file) { const f = document.createElement('div'); f.className = 'cl-file'; f.textContent = '📄 ' + s.file; u.appendChild(f); }
      await wait(700, my);
      const tools = add('<div class="cl-tools"><div class="cl-tools-h"><img class="mm" src="/marketmapr-icon-512.png" alt="">Using MarketMapr</div></div>').firstChild as HTMLElement;
      for (const st of s.steps) {
        const el = document.createElement('div');
        el.className = 'cl-step cl-in';
        el.innerHTML = `<span class="st"></span><span>${st}</span>`;
        tools.appendChild(el);
        scrollDown();
        await wait(900, my);
        el.classList.add('done');
      }
      const h = tools.querySelector('.cl-tools-h');
      if (h && h.lastChild) h.lastChild.textContent = 'Used MarketMapr';
      await wait(500, my);
      for (const b of s.blocks) {
        add(b);
        await wait(Math.min(2600, 700 + b.replace(/<[^>]+>/g, '').length * 6), my);
      }
    }
    function start(i: number, clicked: boolean) {
      const my = ++run;
      (async () => {
        let k = i;
        try {
          for (;;) {
            setActive(k);
            if (!clicked) await waitVisible(my);
            clicked = false;
            await play(k, my);
            if (reduced) return;
            await wait(7000, my);
            k = (k + 1) % SCENES.length;
          }
        } catch { /* replaced by a newer run, or unmounted */ }
      })();
    }
    startRef.current = start;
    const io = new IntersectionObserver((es) => es.forEach((e) => { visible = e.isIntersecting; }), { threshold: 0.3 });
    io.observe(win);
    start(0, false);
    return () => { alive = false; run++; io.disconnect(); };
  }, []);

  return (
    <div className="cl-demo">
      <div className="cl-tabs" role="tablist" aria-label="Examples">
        {SCENES.map((s, i) => (
          <button key={s.tab} type="button" role="tab" className="cl-tab" aria-selected={active === i}
            onClick={() => startRef.current(i, true)}>
            <span className="num">{i + 1}</span>{s.tab}
          </button>
        ))}
      </div>
      <div className="cl-win" ref={winRef}>
        <div className="cl-bar">
          <div className="cl-dots" aria-hidden="true"><i /><i /><i /></div>
          <div className="cl-title">Claude</div>
          <span className="cl-conn"><i />MarketMapr connected</span>
        </div>
        <div className="cl-body" ref={bodyRef}>
          <div className="cl-thread" ref={threadRef} aria-live="polite">
            <div className="cl-hello"><h3>What are we working on today?</h3>MarketMapr is connected</div>
          </div>
        </div>
        <div className="cl-input">
          <div className="cl-box">
            <div className="cl-text" ref={textRef} data-ph="Reply to Claude…" />
            <div className="cl-tools-row"><span className="cl-plus" aria-hidden="true">+</span><span className="cl-send" ref={sendRef} aria-hidden="true">↑</span></div>
          </div>
        </div>
      </div>
      <p className="cl-foot">Examples use made-up people and companies.</p>
    </div>
  );
};

export default ClaudeChatDemo;
